import { jsx, jsxs } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { CheckmarkIcon } from '@flex-design-system/legacy-flex-icons';
import { composeEventHandlers } from '@radix-ui/primitive';
import classNames from 'classnames';
import { CommandItem } from 'cmdk';
import { forwardRef } from 'react';
import { SelectSlot } from '../../../shared/components/slot/Slot.mjs';
import { usePreventMouseDown } from '../../../shared/hooks/usePreventMouseDown.mjs';
import { option, optionClickable, optionContent } from '../../../shared/styles/option.vanilla.mjs';
import { emptyValueAnnouncer } from '../../../shared/utils/empty-value-announcer.mjs';
import { useSingleSelectContext } from '../../contexts/context.mjs';
import { SingleSelectOptionContextProvider } from '../../contexts/option-context.mjs';
import { SelectOptionIndicator as SingleSelectOptionIndicator } from '../option-indicator/OptionIndicator.mjs';
import { singleSelectOptionIndicatorSlot } from './option.vanilla.mjs';

const SingleSelectOption = forwardRef(
  ({
    children,
    className: classNameFromProps,
    indicator,
    leftSlot,
    rightSlot,
    value: valueFromProps,
    onSelect,
    onMouseDown,
    ...props
  }, forwardedRef) => {
    const {
      value: valueFromContext,
      onOpenChange,
      onValueChange,
      closeOnSelect
    } = useSingleSelectContext("Option");
    const preventMouseDown = usePreventMouseDown({ closeOnSelect });
    const className = classNames(
      option,
      !props.disabled && optionClickable,
      classNameFromProps
    );
    const isSelectedOption = valueFromProps === valueFromContext;
    const handleSelect = composeEventHandlers(onSelect, (selectedValue) => {
      onValueChange(selectedValue);
      if (closeOnSelect) {
        onOpenChange(false);
      }
    });
    const handleMouseDown = composeEventHandlers(
      onMouseDown,
      preventMouseDown.onMouseDown
    );
    emptyValueAnnouncer(valueFromProps, "Select");
    return /* @__PURE__ */ jsx(SingleSelectOptionContextProvider, { selected: isSelectedOption, children: /* @__PURE__ */ jsxs(
      CommandItem,
      {
        "data-scope": "single-select",
        "data-part": "option",
        className,
        value: valueFromProps,
        onSelect: handleSelect,
        onMouseDown: handleMouseDown,
        ref: forwardedRef,
        ...props,
        children: [
          /* @__PURE__ */ jsx(
            SelectSlot,
            {
              "data-scope": "single-select",
              "data-part": "option-indicator-slot",
              className: singleSelectOptionIndicatorSlot,
              children: isNil(indicator) ? /* @__PURE__ */ jsx(SingleSelectOptionIndicator, { children: /* @__PURE__ */ jsx(
                SelectSlot,
                {
                  "data-scope": "single-select",
                  "data-part": "option-indicator-checkmark-icon",
                  children: /* @__PURE__ */ jsx(CheckmarkIcon, { size: 22, color: "green" })
                }
              ) }) : indicator
            }
          ),
          /* @__PURE__ */ jsx(
            SelectSlot,
            {
              "data-scope": "single-select",
              "data-part": "option-left-slot",
              side: "left",
              children: leftSlot
            }
          ),
          /* @__PURE__ */ jsx(
            "div",
            {
              "data-scope": "single-select",
              "data-part": "option-content",
              className: optionContent,
              children
            }
          ),
          /* @__PURE__ */ jsx(
            SelectSlot,
            {
              "data-scope": "single-select",
              "data-part": "option-right-slot",
              side: "right",
              children: rightSlot
            }
          )
        ]
      }
    ) });
  }
);

export { SingleSelectOption as SelectOption };
//# sourceMappingURL=Option.mjs.map
